// neo_cogym — 동영상 플레이어 (P1)
// Cloudflare Stream HLS(m3u8). Safari(iPad/맥)는 네이티브 HLS, 그 외는 hls.js.
// 시청% 진도(throttle) 보고 → DB progress.watched_percent.

import type { ProgressUpdate } from "./types";

export interface VideoPlayerOptions {
  src: string; // HLS m3u8
  video: HTMLVideoElement;
  contentAssetId: string;
  durationSec?: number;
  captions?: { lang: string; url: string }[];
  onProgress?: (p: ProgressUpdate) => void;
  /** 완료 간주 임계치(기본 0.95) */
  completeThreshold?: number;
}

export async function attachVideoPlayer(opts: VideoPlayerOptions): Promise<() => void> {
  const { video, src } = opts;
  let cleanupHls: (() => void) | null = null;

  // Safari 등 네이티브 HLS 지원 시 직접 src, 아니면 hls.js
  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
  } else {
    const Hls = (await import("hls.js")).default;
    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
      cleanupHls = () => hls.destroy();
    } else {
      video.src = src; // 최후 폴백
    }
  }

  // 자막(VTT) 트랙
  for (const c of opts.captions ?? []) {
    const track = document.createElement("track");
    track.kind = "subtitles";
    track.srclang = c.lang;
    track.src = c.url;
    video.appendChild(track);
  }

  const threshold = opts.completeThreshold ?? 0.95;
  let lastReport = 0;
  const onTime = () => {
    const dur = video.duration || opts.durationSec || 0;
    if (!dur) return;
    const now = Date.now();
    if (now - lastReport < 5000) return; // 5초 throttle
    lastReport = now;
    const watchedPercent = Math.round((video.currentTime / dur) * 10000) / 100;
    opts.onProgress?.({
      contentAssetId: opts.contentAssetId,
      positionSec: Math.floor(video.currentTime),
      watchedPercent,
      completed: video.currentTime / dur >= threshold,
    });
  };
  video.addEventListener("timeupdate", onTime);
  video.addEventListener("ended", onTime);

  return () => {
    video.removeEventListener("timeupdate", onTime);
    video.removeEventListener("ended", onTime);
    cleanupHls?.();
  };
}
